/**
 * "이 슬롯은 움직이는 그림인가" 판정 — 웹(재생)과 관리자(편집)가 같은 규칙을 봐야 한다.
 *
 * 왜 shared 인가: 관리자에만 정규식이 박혀 있었고 `npc.*` 와 `avatar.player.*` 만 인정했다.
 * 그래서 히어로 걷기 63장(`hero.base.explorer_a.down`, `hero.look.b1_o0.side` 등)이
 * 움직임 편집 목록에서 통째로 빠져 운영자가 손댈 수 없었다. 웹의 `loadCharAnims` 는
 * `<캐릭터키>.<방향>` 을 전부 재생하는데 관리자만 못 보던 것 — 규칙이 갈렸던 게 원인이다.
 */

/** 방향별 걷기 스트립의 방향 이름. 웹 loadCharAnims 와 동일. */
export const WALK_DIRS = ["down", "up", "side"] as const;
export type WalkDir = (typeof WALK_DIRS)[number];

/** 방향 걷기 스트립을 가질 수 있는 캐릭터 키 앞부분. 새 캐릭터군을 추가하면 여기에 넣는다. */
const CHAR_KEY_RE = /^(npc\.[a-z0-9_]+|avatar\.player|hero\.base\.[a-z0-9_]+|hero\.look\.[a-z0-9_]+)$/;

/** `<캐릭터키>.<방향>` 형태의 걷기 스트립인가. */
export function isWalkStripKey(key: string): boolean {
  const i = key.lastIndexOf(".");
  if (i < 0) return false;
  const dir = key.slice(i + 1);
  if (!(WALK_DIRS as readonly string[]).includes(dir)) return false;
  return CHAR_KEY_RE.test(key.slice(0, i));
}

/** 편집기가 다룰 수 있는 애니 슬롯인가 — 루프 스트립(`<키>.strip`) 또는 방향 걷기. */
export function isAnimSlotKey(key: string): boolean {
  return key.endsWith(".strip") || isWalkStripKey(key);
}

// ── 제자리(대기) 동작 ────────────────────────────────────────
/**
 * 서 있을 때 재생할 프레임 — **걷기 스트립을 그대로 쓴다**(대기 그림 따로 안 그린다).
 *
 * 4프레임 걷기 사이클의 구조를 실측했다:
 * - **0·2번 = 걷는 중**(다리가 벌어진 접지 자세)
 * - **1·3번 = 서 있는 자세**(다리를 모은 통과 자세)
 *
 * 예전엔 대기를 **0번 한 장으로 고정**해서, 가만히 서 있는 아이가
 * **걷다 멈춘 자세로 얼어** 있었다. 1·3번을 느리게 오가면 두 가지가 한 번에 해결된다 —
 * 자세가 바로 서고, 무게중심이 살짝 옮겨 가며 숨 쉬는 것처럼 보인다.
 *
 * 픽셀 차이 실측: 0↔1 은 21~28(걸음이 보인다), **1↔3 은 3.5~9.4**(자세는 그대로, 미묘하게 움직인다).
 *
 * 프레임이 4장보다 적은 스트립은 고를 게 없으므로 0번 한 장(예전 동작 그대로).
 *
 * ## 🔴 이 숫자는 **그림의 칸 순서와 짝이다**
 * 표준 걷기 한 바퀴는 `접지 → 중간 → 접지(반대발) → 중간` 이라 **1·3번이 발이 모인 칸**이다.
 * 그림이 한 칸 밀려 들어오면 여기가 **걷는 중** 자세가 되어, 가만히 선 아이가
 * 걷다 만 자세로 얼어 있는다 — **걸을 때는 멀쩡해 보여서 아무도 못 알아챈다**
 * (2026-08-27 에 여섯 장이 그 상태로 운영에 나가 있었다).
 *
 * 그래서 **그림 쪽을 검사한다**: `pnpm art:idle` 이 옆모습 다리 폭을 재서 밀린 것을 잡고,
 * `scripts/rotate-strip.mjs` 로 세 방향을 함께 돌려 고친다.
 * 이 값을 바꾸려면 **그림 스물여섯 장을 같이** 봐야 한다(문서: docs/build/10 §4-1).
 */
export function idleFrameIndices(frameCount: number): number[] {
  return frameCount >= 4 ? [1, 3] : [0];
}

/**
 * 대기 재생 속도(fps). 두 장을 오가므로 한 사이클은 이 값의 절반 Hz —
 * 1.2fps = **0.6Hz** 로 시각 안전 기준(3Hz 미만, FR-COM-06)에 한참 못 미친다.
 */
export const IDLE_FPS = 1.2;

/* ── 계속 도는 장식 루프 속도 ──────────────────────────── */

/**
 * 🔴 **가만히 있어도 계속 도는 그림은 3Hz 미만이어야 한다**(시각 안전, 불변식 6).
 * 걷기 스트립(~8fps)과 다르다 — 걷기는 아이가 **움직일 때만** 돈다.
 *
 * 세 곳이 이 규칙을 쓴다: 첫 화면 마스코트 · 체험공간 장식 · AR 스트립.
 * 예전엔 **셋 다 따로** `Math.max(0.2, Math.min(3, …))` 를 적어 뒀다 — 기준이 바뀌면
 * 한 곳만 고쳐지고 나머지는 조용히 남는다.
 *
 * 예전엔 이 값이 아이 화면(「landingAssets」)에만 있었다. 그래서 관리자 애니 스튜디오는
 * 8fps 까지 밀 수 있었고, **미리보기는 6fps 인데 아이 화면은 3fps** 로 나왔다 —
 * 운영자는 왜 느린지 알 수가 없다.
 */
export const LOOP_FPS_MIN = 0.2;
export const LOOP_FPS_MAX = 3;

export function clampLoopFps(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v)
    ? Math.max(LOOP_FPS_MIN, Math.min(LOOP_FPS_MAX, v))
    : undefined;
}

/** 이 자리가 **첫 화면에서 계속 도는** 그림인가(마스코트·배경). */
export function isLandingSlotKey(key: string): boolean {
  return key.trim().startsWith("landing.");
}

// ── 걷기 재생 속도 ───────────────────────────────────────────
/**
 * **전속력에서의 배속.** 1.0 이 아닌 이유는 실측 때문이다:
 * 한 사이클(4프레임 ÷ 8fps = 0.5초)에 몸은 85px 이동하는데 그림 속 두 걸음 보폭은 약 78px 이다.
 * 그대로 두면 걸음마다 발이 앞으로 약 10% 끌린다.
 */
export const WALK_RATE_AT_FULL_SPEED = 1.1;
/**
 * 하한 — **0 을 막는 안전장치일 뿐** 이다. 일부러 높게 잡으면 안 된다.
 *
 * 이 식은 자기완결적이다: 12px/s 로 기어가면 배속 0.08 이 되고, 그러면 한 사이클이
 * 6.4초가 되며 그동안 몸은 77px 이동한다 — 그림 보폭(78px)과 맞는다. 즉 **느린 걸음은
 * 느린 애니가 정답**이다. 처음에 0.4 로 잡았다가 되돌렸는데, 그 값이면 천천히 걷는
 * NPC(26px/s)가 배속 0.4 로 강제돼 발이 두 배 가까이 끌린다.
 */
export const WALK_RATE_MIN = 0.08;
/** 상한 — 밀려나는 등 이상 속도에서 다리가 미친 듯이 돌아가지 않게. */
export const WALK_RATE_MAX = 1.8;

/**
 * 이동 속도에 비례한 걷기 애니 배속.
 *
 * 예전엔 8fps 고정이라 **조이스틱을 살살 밀어도 다리는 전속력으로** 움직였다.
 * 아날로그 입력에서는 속도가 연속으로 변하므로 배속도 같이 변해야 발이 땅을 잡는다.
 */
export function walkAnimRate(speed: number, fullSpeed: number): number {
  if (!(fullSpeed > 0) || !Number.isFinite(speed)) return 1;
  const r = (Math.abs(speed) / fullSpeed) * WALK_RATE_AT_FULL_SPEED;
  return Math.max(WALK_RATE_MIN, Math.min(WALK_RATE_MAX, r));
}

/**
 * **그 표시 크기에서 배속 1.0 이 되는 속도.**
 *
 * 보폭은 그림 크기에 비례한다. 히어로(80px)에서 맞춰 둔 기준 속도를 56px 짜리 NPC 에
 * 그대로 쓰면 보폭이 30% 작은데 같은 속도로 재는 셈이라 발이 끌린다.
 */
export function matchedWalkSpeed(displayPx: number, refPx: number, refSpeed: number): number {
  if (!(refPx > 0) || !(displayPx > 0)) return refSpeed;
  return refSpeed * (displayPx / refPx);
}

/**
 * 걷기로 볼 최소 속도(px/s).
 *
 * 예전엔 **입력**으로 걷기/대기를 갈랐는데(`len > 0.01`), 관성이 있어서 손을 떼도
 * 몸은 0.27초쯤 더 미끄러진다 → **선 자세로 스르륵 미끄러지는** 모습이 됐다.
 * 실제 속도로 갈라야 발이 멈출 때 애니도 멈춘다.
 */
export const MOVING_MIN_SPEED = 12;

// ── 걷기 상하 반동 ───────────────────────────────────────────
/**
 * 걷기 반동 진폭 — 캐릭터 키 대비 비율.
 *
 * **왜 코드로 하나**: 걷기 스트립을 실측하니 사이클 상하 반동이 **0px** 이었다
 * (히어로 룩·프리셋 모두 네 프레임 머리 높이가 완전히 같다). 발만 움직이고 몸은
 * 레일 위를 미끄러지듯 간다 — 프레임을 8장으로 늘려도 **평평한 채로 부드러워질 뿐**이다.
 *
 * 그림에 구워 넣을 수도 없다: 프레임 위아래 여백이 **2px** 뿐이라 띄울 자리가 없다.
 * 그래서 스프라이트를 실제로 들어 올린다(점프와 같은 방식이라 그림자는 바닥에 남는다).
 *
 * 3% 는 사람 걸음의 실제 상하 진폭과 비슷하다(170cm 기준 약 4~5cm).
 */
export const WALK_BOB_RATIO = 0.03;

/**
 * 사이클 진행도(0~1) → 지면에서 띄울 정도(0~1).
 *
 * 한 사이클에 두 걸음이므로 봉우리도 두 개다. **접지 프레임에서 가장 낮고
 * 통과 프레임에서 가장 높다** — 발이 땅을 짚을 때 몸이 내려앉는 게 자연스럽다.
 */
export function walkBobAt(progress: number): number {
  if (!Number.isFinite(progress)) return 0;
  return Math.abs(Math.sin(2 * Math.PI * progress));
}

/**
 * 발이 땅을 짚는 프레임(0부터).
 *
 * 실측: 4프레임 사이클에서 **0·2번은 두 발이 38.8px 벌어진 접지 자세**,
 * 1·3번은 17~21px 로 모인 통과 자세다. 발소리·먼지를 여기에 맞춰야
 * 소리와 발이 같이 떨어진다(예전엔 220ms 타이머라 발과 무관하게 났다).
 */
export function contactFrameIndices(frameCount: number): number[] {
  if (frameCount < 2) return [0];
  return [0, Math.floor(frameCount / 2)];
}

// ── 방향 고르기 ──────────────────────────────────────────────
export type FaceDir = "up" | "down" | "left" | "right";

/**
 * 이동 벡터 → 바라볼 방향. **지금 방향을 우대**해서 대각선에서 깜빡이지 않게 한다.
 *
 * 예전엔 `|vx| > |vy|` 로만 갈라서, 조이스틱을 45°로 물고 있으면 미세한 흔들림에
 * `side ↔ down` 이 매 프레임 뒤집혔다. 히스테리시스(관성)를 주면 한쪽으로 확실히
 * 기울 때만 바뀐다.
 *
 * @param bias 지금 방향을 얼마나 우대할지(1 = 우대 없음 = 예전 동작).
 */
export function pickFaceDir(vx: number, vy: number, current: FaceDir, bias = 1.35): FaceDir {
  const h = Math.abs(vx);
  const v = Math.abs(vy);
  const nowHoriz = current === "left" || current === "right";
  // 지금 가로를 보고 있으면 세로가 bias 배 넘게 커져야 세로로 바뀐다(반대도 같다)
  const useHoriz = nowHoriz ? h * bias >= v : h >= v * bias;
  if (useHoriz) return vx < 0 ? "left" : "right";
  return vy < 0 ? "up" : "down";
}

/** 걷기 스트립 키에서 캐릭터 키만 떼어낸다(`npc.greg_white.down` → `npc.greg_white`). */
export function walkStripCharKey(key: string): string | undefined {
  if (!isWalkStripKey(key)) return undefined;
  return key.slice(0, key.lastIndexOf("."));
}

// ── 사람이 읽는 이름표 ────────────────────────────────────────
// `npc.greg_white.down` 같은 키를 운영자에게 그대로 보여주면 무엇인지 알 수 없다.
// 초등학생도 알아볼 수 있게 "그레그 · 앞으로 걷기" 로 바꾼다.

const DIR_LABEL: Record<string, string> = { down: "앞으로 걷기", up: "뒤로 걷기", side: "옆으로 걷기" };

/** 캐릭터 키 → 사람이 읽는 이름. 모르는 캐릭터는 키 뒷부분을 그대로 쓴다(새 캐릭터도 깨지지 않게). */
export function charKeyLabel(charKey: string): string {
  const known: Record<string, string> = {
    "npc.greg_white": "그레그",
    "npc.chick": "병아리",
    "npc.greg_cat": "검은 고양이",
    "avatar.player": "기본 플레이어",
  };
  if (known[charKey]) return known[charKey]!;
  const preset = /^hero\.base\.explorer_([a-e])$/.exec(charKey);
  if (preset) return `탐험가 ${"abcde".indexOf(preset[1]!) + 1}`;
  const look = /^hero\.look\.b(\d)_o(\d)$/.exec(charKey);
  if (look) {
    const outfit = ["기본", "겨울", "탐험가", "우비"][Number(look[2])] ?? `옷${look[2]}`;
    return `아이 ${look[1]} · ${outfit}`;
  }
  return charKey.split(".").slice(-1)[0] ?? charKey;
}

/** 애니 슬롯 키 → "그레그 · 앞으로 걷기" 같은 이름표. */
export function animSlotLabel(key: string): string {
  const charKey = walkStripCharKey(key);
  if (charKey) return `${charKeyLabel(charKey)} · ${DIR_LABEL[key.slice(key.lastIndexOf(".") + 1)]}`;
  if (key.endsWith(".strip")) {
    const base = key.slice(0, -".strip".length);
    if (base.startsWith("landing.char.")) {
      const id = base.slice("landing.char.".length);
      return `메인 화면 ${id === "greg" ? "왼쪽" : id === "chick" ? "오른쪽" : id} 캐릭터 · 제자리 움직임`;
    }
    if (base.startsWith("decor.")) return `장식 ${base.slice("decor.".length)} · 반짝임`;
    return `${base} · 제자리 움직임`;
  }
  return key;
}

/**
 * 편집기 제안 목록 — **카탈로그에서 전부 뽑는다**(하드코딩 금지).
 * 예전엔 7줄이 손으로 박혀 있어 히어로가 통째로 빠졌다. 캐릭터를 추가하면 여기로 저절로 들어온다.
 */
export function animSlotSuggestions(opts: {
  npcIds: readonly string[];
  presetIds: readonly string[];
  lookIds: readonly string[];
  loopKeys?: readonly string[];
}): { key: string; label: string }[] {
  const out: { key: string; label: string }[] = [];
  const push = (k: string) => out.push({ key: k, label: animSlotLabel(k) });
  for (const id of opts.npcIds) for (const d of WALK_DIRS) push(`npc.${id}.${d}`);
  for (const d of WALK_DIRS) push(`avatar.player.${d}`);
  for (const id of opts.presetIds) for (const d of WALK_DIRS) push(`hero.base.${id}.${d}`);
  for (const id of opts.lookIds) for (const d of WALK_DIRS) push(`hero.look.${id}.${d}`);
  for (const k of opts.loopKeys ?? []) push(k);
  return out;
}

/**
 * 스프라이트를 **들어 올린 y** 와 **그만큼의 리프트** 를 한 번에 돌려준다.
 *
 * ## 왜 값 두 개를 한 함수가 돌려주는가
 * 월드는 스프라이트의 y 하나에 세 가지를 겹쳐 쓴다: 바닥 위치, 점프 높이, 걷기 반동.
 * 그래서 다음 프레임에 "진짜 바닥이 어디였지"를 되찾으려면, 이번에 **올린 만큼**을
 * 정확히 기억해 둬야 한다.
 *
 * 예전엔 올리는 줄과 기억하는 줄이 따로 있었고(WorldScene 962·963줄) 서로 어긋났다 —
 * 올릴 땐 `점프 + 반동`을 올리면서 기억은 `점프`만 했다. 되찾은 바닥이 매 프레임 반동만큼
 * 위로 밀려서, 걸을수록 캐릭터가 조금씩 떠올랐다(좌우로 걸을 때 특히 잘 보인다 —
 * 위아래로 걸을 땐 벽 충돌이 어느 정도 되돌려 준다).
 *
 * 두 값을 **같은 계산에서** 함께 내보내면 애초에 어긋날 수가 없다. 호출부가 규칙을
 * 다시 적을 일이 없으니, 나중에 리프트가 하나 더 늘어도 여기만 고치면 된다.
 */
export function applyLift(
  groundY: number,
  parts: { jumpH?: number; bob?: number; footFix?: number },
): { y: number; lift: number } {
  const lift = (parts.jumpH ?? 0) + (parts.bob ?? 0) - (parts.footFix ?? 0);
  return { y: groundY - lift, lift };
}

/** 올려 둔 스프라이트 y → 진짜 바닥 y. `applyLift` 가 준 `lift` 를 그대로 넘긴다. */
export function groundFromSprite(spriteY: number, lift: number): number {
  return spriteY + lift;
}
